import { Button, Flex, Heading, Spacer } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { NavLink } from "react-router-dom";
import { CedarEditor } from "src/components/CedarEditor";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { StickyBottom } from "src/components/Layout/StickyBottom";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import {
  PolicyManagementError,
  usePolicyManagement,
} from "src/hooks/usePolicyManagement";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";

type EditPolicyContainerProps = {
  organization: { id: string; path: string };
  initialPolicy: string;
};

export const EditPolicyContainer = (props: EditPolicyContainerProps) => {
  const toast = useAvelaToast();
  const [submitting, setSubmitting] = useState(false);
  const [policy, setPolicy] = useState(props.initialPolicy);

  const navigate = useNavigate();

  const { initialPolicy, organization } = props;

  const { storePolicy } = usePolicyManagement({
    type: "DEFAULT",
    organizationId: organization.id,
  });

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const key = `organizations/${organization.id}.cedar`;
      await storePolicy(key, policy);

      toast({
        id: "update-organization",
        title: "Organization updated",
        isClosable: true,
        status: "info",
      });

      navigate(Url.OrgAdmin.Org.settings(organization, "security"));
    } catch (err) {
      const description = (): React.ReactNode => {
        if (err instanceof PolicyManagementError) {
          return err.message;
        }
        return "Check your network and try again.";
      };
      toast({
        id: "update-organization",
        title: `Something went wrong!`,
        description: description(),
        isClosable: true,
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  }, [storePolicy, toast, navigate, organization, policy]);

  return (
    <>
      <CedarEditor
        height="60vh"
        initialPolicy={initialPolicy}
        onChangePolicy={(value) => setPolicy(value || "")}
      />
      <Spacer minHeight={6} />
      <StickyBottom gap={6} justifyContent="space-between">
        <Flex align="center" gap={6}>
          <Spacer />
          <Button
            as={NavLink}
            to={Url.OrgAdmin.Org.settings(organization, "security")}
            variant="outline"
            colorScheme="gray"
          >
            Back
          </Button>
        </Flex>
        <Button type="submit" isLoading={submitting} onClick={handleSubmit}>
          Save access policies
        </Button>
      </StickyBottom>
    </>
  );
};

export const EditPolicy = () => {
  const organizationRD = useOrganization();

  const { policy: policyRemoteData } = usePolicyManagement({
    type: "GET_POLICY",
    key: organizationRD.hasData()
      ? `organizations/${organizationRD.data.id}.cedar`
      : "",
    organizationId: organizationRD.toNullable()?.id ?? "",
    skip: !organizationRD.hasData(),
  });

  return (
    <RemoteDataView
      remoteData={RD.toTuple(organizationRD, policyRemoteData)}
      error={() => <GenericError />}
    >
      {([organization, initialPolicy]) => (
        <Flex direction="column" height="100%">
          <Heading as="h1" fontSize="2xl" fontWeight="600">
            Edit access policies
          </Heading>
          <Spacer minHeight={12} />
          <EditPolicyContainer
            organization={organization}
            initialPolicy={initialPolicy}
          />
        </Flex>
      )}
    </RemoteDataView>
  );
};
